/**
 * Sticky public header — condenses on scroll (CSS class toggle driven by scrollY),
 * gold brand mark, in-page anchor nav, Donate CTA, accessible mobile menu.
 */
import { useEffect, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from '../ui/Button.jsx';
import styles from './Header.module.css';

// Distinct page links only — no in-page anchor "bookmarks".
const NAV = [
  { label: 'What we do', to: '/programs' },
  { label: 'Our work', to: '/served' },
  { label: 'Transparency', to: '/transparency' },
  { label: 'Get involved', to: '/get-involved' },
  { label: 'About', to: '/about' },
  { label: 'Contact', to: '/contact' },
];

export function Header() {
  const [condensed, setCondensed] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setCondensed(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header className={`${styles.header} ${condensed ? styles.condensed : ''}`}>
      <div className={`container ${styles.inner}`}>
        <Link to="/" className={styles.brand} aria-label="USMEHAR CARE home">
          <span className={styles.mark} aria-hidden="true">
            <span className={styles.markRing} />
            <span className={styles.markDot} />
          </span>
          <span className={styles.wordmark}>
            USMEHAR <span className={styles.wordmarkAccent}>CARE</span>
          </span>
        </Link>

        <nav className={styles.nav} aria-label="Primary">
          {NAV.map((item) => (
            <NavLink
              key={item.label}
              to={item.to}
              className={({ isActive }) =>
                `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className={styles.actions}>
          <Button to="/donate" variant="gold" size="sm">
            Donate
          </Button>
          <button
            className={styles.menuToggle}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <nav className={styles.mobileNav} aria-label="Mobile">
          {NAV.map((item) => (
            <NavLink
              key={item.label}
              to={item.to}
              className={({ isActive }) =>
                `${styles.mobileLink} ${isActive ? styles.mobileLinkActive : ''}`
              }
              onClick={() => setMenuOpen(false)}
            >
              {item.label}
            </NavLink>
          ))}
          <Link to="/faq" className={styles.mobileLink} onClick={() => setMenuOpen(false)}>
            FAQ
          </Link>
          <Link to="/donate" className={styles.mobileLink} onClick={() => setMenuOpen(false)}>
            Donate
          </Link>
        </nav>
      )}
    </header>
  );
}

export default Header;
